import React, { useState } from 'react';
import axios from 'axios';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from './SEO';
import { API_BASE } from '../utils/config';
import './ContactPage.css';

const ContactPage = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isLoggedIn = !!user && !!user.id;
    const userName = user.full_name || '';

    const getFirstAndLastName = (fullName) => {
        if (!fullName) return { first: '', last: '' };
        const parts = fullName.trim().split(/\s+/);
        if (parts.length === 1) {
            return { first: parts[0], last: '' };
        }
        return {
            first: parts[0],
            last: parts.slice(1).join(' ')
        };
    };

    const nameParts = getFirstAndLastName(user.full_name);

    const [formData, setFormData] = useState({
        first_name: isLoggedIn ? nameParts.first : '',
        last_name: isLoggedIn ? nameParts.last : '',
        email: isLoggedIn ? (user.email || '') : '',
        phone: isLoggedIn ? (user.phone_number || user.phone || '') : '',
        message: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); // { type: 'success' | 'error', text: string }

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'phone') {
            const onlyNums = value.replace(/[^0-9]/g, '');
            setFormData((prev) => ({
                ...prev,
                [name]: onlyNums
            }));
            if (errors.phone) {
                setErrors((prev) => ({ ...prev, phone: '' }));
            }
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // First Name
        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required.';
        } else if (!/^[A-Za-z\s'-]+$/.test(formData.first_name.trim())) {
            newErrors.first_name = 'First name can only contain letters, spaces, hyphens, or apostrophes.';
        }

        // Last Name
        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last name is required.';
        } else if (!/^[A-Za-z\s'-]+$/.test(formData.last_name.trim())) {
            newErrors.last_name = 'Last name can only contain letters, spaces, hyphens, or apostrophes.';
        }

        // Email
        if (!formData.email.trim()) {
            newErrors.email = 'Work email is required.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            newErrors.email = 'Please enter a valid email address.';
        }

        // Phone (optional, but if filled must be valid)
        if (formData.phone) {
            if (!/^\d{10,15}$/.test(formData.phone)) {
                newErrors.phone = 'Phone number must be between 10 and 15 digits.';
            }
        }

        // Message
        if (!formData.message.trim()) {
            newErrors.message = 'Message is required.';
        } else if (formData.message.trim().length < 10) {
            newErrors.message = 'Message must be at least 10 characters long.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus(null);

        if (!validateForm()) {
            setStatus({
                type: 'error',
                text: 'Please correct the validation errors in the form.'
            });
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${API_BASE}/api/contact`, formData);
            if (response.data.success) {
                setStatus({
                    type: 'success',
                    text: 'Thank you! Your demo request has been received. Our team will contact you shortly.'
                });
                setFormData({
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    message: ''
                });
                setErrors({});
            }
        } catch (error) {
            console.error('Submit contact failed:', error);
            setStatus({
                type: 'error',
                text: error.response?.data?.error || 'Failed to submit form. Please check your network and try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="contact-page-container">
            <SEO
                title="Contact & Schedule a Demo - Sales & Support"
                description="Get in touch with the Smart Interview Platform team to request a personalized 1-on-1 demo or get corporate technical support."
                keywords="request demo, interview support, corporate contact, SaaS sales, recruitment software help"
            />

            {/* Schema.org LocalBusiness Structured Data for Local SEO */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "LocalBusiness",
                    "name": "Smart Interview HQ",
                    "image": "https://smartinterviewplatform.com/og_cover_banner.png",
                    "telephone": "+91-80-4967-1000",
                    "email": "support@smartinterviewplatform.com",
                    "address": {
                        "@type": "PostalAddress",
                        "streetAddress": "Ajinkya Park, Rachna Eternia, Pan Card Club Rd, Baner",
                        "addressLocality": "Pune",
                        "addressRegion": "Maharashtra",
                        "postalCode": "411069",
                        "addressCountry": "IN"
                    },
                    "geo": {
                        "@type": "GeoCoordinates",
                        "latitude": 18.5590,
                        "longitude": 73.7797
                    },
                    "url": "https://smartinterviewplatform.com/contact",
                    "openingHoursSpecification": {
                        "@type": "OpeningHoursSpecification",
                        "dayOfWeek": [
                            "Monday",
                            "Tuesday",
                            "Wednesday",
                            "Thursday",
                            "Friday"
                        ],
                        "opens": "09:00",
                        "closes": "18:00"
                    }
                })}
            </script>
            <Navbar isLoggedIn={isLoggedIn} userName={userName} />

            <main className="contact-content-wrapper">
                <div className="contact-grid">
                    {/* Info Side */}
                    <div className="contact-info-panel">
                        <span className="contact-badge">Get In Touch</span>
                        <h1>Accelerate Your Talent Assessment</h1>
                        <p>
                            Schedule a personalized 1-on-1 walkthrough. See how our isolated tenant databases and AI interviewing engine fit your recruiting pipeline.
                        </p>

                        <div className="contact-details-list">
                            <div className="contact-detail-item">
                                <div className="contact-detail-icon">🏢</div>
                                <div>
                                    <h3>Enterprise Ready</h3>
                                    <p>Fully compliant multi-tenant databases with custom subdomain bindings.</p>
                                </div>
                            </div>
                            <div className="contact-detail-item">
                                <div className="contact-detail-icon">🔒</div>
                                <div>
                                    <h3>Secure Encryption</h3>
                                    <p>All sensitive contact info and interview transcripts are encrypted via AES-256-GCM.</p>
                                </div>
                            </div>
                            <div className="contact-detail-item">
                                <div className="contact-detail-icon">📍</div>
                                <div>
                                    <h3>Global HQ Address</h3>
                                    <p>Ajinkya Park, Rachna Eternia, Pan Card Club Rd, Baner, Pune, Maharashtra 411069</p>
                                </div>
                            </div>
                            <div className="contact-detail-item">
                                <div className="contact-detail-icon">📞</div>
                                <div>
                                    <h3>Support Hotline</h3>
                                    <p> admin@arkalaksh.com </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Side */}
                    <div className="contact-form-panel">
                        <div className="contact-form-card">
                            <h2>Request a Demo / Support</h2>
                            <p>Fill out the form below and our team will reach out within 24 hours.</p>

                            {status && (
                                <div className={`contact-status-banner ${status.type}`}>
                                    {status.type === 'success' ? '✓ ' : '⚠️ '}
                                    {status.text}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="contact-form" noValidate>
                                <div className="contact-form-row">
                                    <div className="contact-form-group">
                                        <label htmlFor="first_name">First Name *</label>
                                        <input
                                            type="text"
                                            id="first_name"
                                            name="first_name"
                                            value={formData.first_name}
                                            onChange={handleChange}
                                            required
                                            placeholder="John"
                                        />
                                        {errors.first_name && <span className="contact-form-error">{errors.first_name}</span>}
                                    </div>
                                    <div className="contact-form-group">
                                        <label htmlFor="last_name">Last Name *</label>
                                        <input
                                            type="text"
                                            id="last_name"
                                            name="last_name"
                                            value={formData.last_name}
                                            onChange={handleChange}
                                            required
                                            placeholder="Doe"
                                        />
                                        {errors.last_name && <span className="contact-form-error">{errors.last_name}</span>}
                                    </div>
                                </div>

                                <div className="contact-form-group">
                                    <label htmlFor="email">Work Email *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        placeholder="john.doe@company.com"
                                    />
                                    {errors.email && <span className="contact-form-error">{errors.email}</span>}
                                </div>

                                <div className="contact-form-group">
                                    <label htmlFor="phone">Phone Number (Optional)</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="5550000000"
                                    />
                                    {errors.phone && <span className="contact-form-error">{errors.phone}</span>}
                                </div>

                                <div className="contact-form-group">
                                    <label htmlFor="message">Message *</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows="5"
                                        placeholder="Tell us about your organization and hiring needs..."
                                    ></textarea>
                                    {errors.message && <span className="contact-form-error">{errors.message}</span>}
                                </div>

                                <button type="submit" className="contact-submit-btn" disabled={loading}>
                                    {loading ? 'Submitting...' : 'Send Request'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default ContactPage;
